import React, { type Dispatch, type SetStateAction, useState } from "react";

import { useHistory } from "react-router-dom";
import { InsightUserStatus } from "@doitintl/cmp-models";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useApiContext } from "../../../api/context";
import { useErrorSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { queryKeys } from "../../../constants";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { consoleErrorWithSentry } from "../../../utils";
import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { postInsightEnhancement } from "../api";
import { type Insight } from "../types";
import DismissInsightModal from "./DismissInsightModal";
import EnhanceInsightModal, { type InsightEnhancementData } from "./EnhanceInsightModal";

type MenuOption = {
  label: string;
  show: boolean;
  action: () => Promise<void> | void;
};

type Props = {
  selectedInsight: Insight | null;
  updateInsight: Dispatch<SetStateAction<Insight | null>>;
};

const defaultTags = [];

// TODO: Refactor this component to use the common component for the three dot menu
const InsightThreeDotMenu: React.FC<Props> = ({ selectedInsight, updateInsight }) => {
  const queryClient = useQueryClient();

  const { trackEvent } = useSegmentTrackEvent();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { customer } = useCustomerContext();

  const history = useHistory();

  const open = Boolean(anchorEl);

  const api = useApiContext();

  const { userId, isDoitEmployee } = useAuthContext();
  const showErrorSnackbar = useErrorSnackbar();

  const [isDismissModalVisible, setIsDismissModalVisible] = useState(false);
  const [isEnhanceInsightModalVisible, setIsEnhanceInsightModalVisible] = useState(false);

  const insightStatus = selectedInsight?.userStatusChanges?.status;

  const isCustomInsight = selectedInsight?.providerId === "custom";

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusUpdate = async (newStatus: InsightUserStatus) => {
    handleClose();

    if (newStatus === InsightUserStatus.DISMISSED) {
      setIsDismissModalVisible(true);
    } else {
      const data = {
        customerId: customer.id,
        providerId: selectedInsight?.providerId,
        key: selectedInsight?.key,
        status: newStatus,
        userId,
      };

      try {
        await api.post("insights/status", data);

        // refresh the insights
        queryClient.invalidateQueries({ queryKey: [customer.id, queryKeys.insights] });

        updateInsight((insight) => {
          if (!insight) {
            return insight;
          }

          return {
            ...insight,
            userStatusChanges: insight.userStatusChanges && {
              ...insight.userStatusChanges,
              status: newStatus,
            },
          };
        });

        trackEvent("Insight Status Changed", {
          customerId: customer.id,
          insightKey: selectedInsight?.key,
          insightProviderId: selectedInsight?.providerId,
          insightStatus: newStatus,
        });
      } catch (e) {
        consoleErrorWithSentry(e);
        showErrorSnackbar("Something went wrong. Please try again later.");
      }
    }
  };

  const handleEditClick = () => {
    const newInsightUrl = `/customers/${customer.id}/optimize/insights/new-insight/${selectedInsight?.key}`;

    history.push(newInsightUrl);
  };

  const handleSaveInsightEnhancement = async (insightEnhancement: InsightEnhancementData) => {
    if (!selectedInsight) {
      return;
    }

    try {
      await postInsightEnhancement(api, {
        customerId: customer.id,
        providerId: selectedInsight.providerId,
        insightKey: selectedInsight.key,
        ...insightEnhancement,
      });

      updateInsight((insight) => {
        if (!insight) {
          return null;
        }
        return {
          ...insight,
          priority: insightEnhancement.priority || null,
          tags: insightEnhancement.tags,
          expertNote: insightEnhancement.expertNote
            ? {
                note: insightEnhancement.expertNote,
                createdAt: new Date().toISOString(),
              }
            : null,
        };
      });
    } catch (err) {
      consoleErrorWithSentry(err);
      showErrorSnackbar("Something went wrong. Please try again later.");
    }
  };

  const menuOptions: MenuOption[] = [
    {
      label: "Mark as actionable",
      show: insightStatus !== InsightUserStatus.ACTIONABLE,
      action: () => handleStatusUpdate(InsightUserStatus.ACTIONABLE),
    },
    {
      label: "Mark as acknowledged",
      show: insightStatus !== InsightUserStatus.ACKNOWLEDGED,
      action: () => handleStatusUpdate(InsightUserStatus.ACKNOWLEDGED),
    },
    {
      label: "Mark as in progress",
      show: insightStatus !== InsightUserStatus.IN_PROGRESS,
      action: () => handleStatusUpdate(InsightUserStatus.IN_PROGRESS),
    },
    {
      label: "Mark as optimized",
      show: insightStatus !== InsightUserStatus.OPTIMIZED,
      action: () => handleStatusUpdate(InsightUserStatus.OPTIMIZED),
    },
    {
      label: "Dismiss",
      show: insightStatus !== InsightUserStatus.DISMISSED,
      action: () => handleStatusUpdate(InsightUserStatus.DISMISSED),
    },
    {
      label: "Edit",
      show: (isDoitEmployee || false) && isCustomInsight,
      action: handleEditClick,
    },
    {
      label: "Enhance",
      show: (isDoitEmployee || false) && !isCustomInsight,
      action: () => {
        setIsEnhanceInsightModalVisible(true);
      },
    },
  ];

  return (
    <>
      <IconButton
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label="actions"
        onClick={handleClick}
        size="medium"
        sx={{ mb: "-4px" }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        {menuOptions.map(({ label, show, action }) =>
          show ? (
            <MenuItem key={`menu-item-${label}`} onClick={action}>
              {label}
            </MenuItem>
          ) : null
        )}
      </Menu>
      {isDismissModalVisible && (
        <DismissInsightModal
          onClose={() => {
            setIsDismissModalVisible(false);
          }}
        />
      )}
      <EnhanceInsightModal
        existingPriority={selectedInsight?.priority ?? ""}
        existingTags={selectedInsight?.tags ?? defaultTags}
        existingExpertNote={selectedInsight?.expertNote?.note ?? ""}
        open={isEnhanceInsightModalVisible}
        onClose={() => {
          setIsEnhanceInsightModalVisible(false);
        }}
        onSave={handleSaveInsightEnhancement}
      />
    </>
  );
};

export default InsightThreeDotMenu;
